"use client";

/**
 * "Never built" survey (PANDA-1245).
 *
 * Catches the user who signed up but never started a build — the cohort whose
 * drop-off we can only guess at. It asks one open question, "What stopped you
 * from building?", on the home/empty state. Faithful to the approved prototype
 * (survey_2_never_built.html): a single free-text field with Skip / Send.
 *
 * Two triggers, mirroring the prototype's dev note:
 *   A. Exit intent — the cursor leaving through the TOP edge of the viewport
 *      (the move toward the tab strip / address bar that precedes leaving). The
 *      same conservative detection as the pre-publish exit survey (PANDA-1162),
 *      so it never fires from normal in-page use.
 *   B. No build 24h after signup — shown as an in-app prompt on a later session
 *      (the 24h gate guarantees it's not the signup session itself).
 *
 * The response is saved in PostHog and forwarded to #panda-feedback (with a link
 * to the session recording) by /api/surveys/never-built. PostHog is the record
 * of truth, so there is no DB table. Shown at most once per suppression window
 * per user, and it stands down whenever any other modal/overlay is open so it
 * never stacks (PANDA-1158).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  hasHigherPriorityModalOpen,
  isAnyModalOpen,
  useHasHigherPriorityModalOpen,
  setModalOpen,
  MODAL_IDS,
} from "@/lib/ui/modal-coordinator";
import {
  getSessionContext,
  trackNeverBuiltSurveyShown,
  trackNeverBuiltSurveySkipped,
  type NeverBuiltTriggerSource,
} from "@/lib/analytics";

/** How close to the very top of the viewport a leave must be to count (px). */
const TOP_EDGE_THRESHOLD_PX = 8;
/** Don't surface the 24h prompt until at least this long after signup. */
const MIN_AGE_FOR_24H_PROMPT_MS = 24 * 60 * 60 * 1000;
/** Once shown, don't surface it again for this many days. */
const SUPPRESS_DAYS = 30;
/** Small settle delay before the 24h prompt appears on load. */
const PROMPT_DELAY_MS = 1200;

const COOKIE_KEY = "panda-never-built-survey";

const OPEN_OVERLAY_SELECTOR =
  '[role="dialog"][data-open],[role="alertdialog"][data-open],[role="menu"][data-open],[role="listbox"][data-open]';

/** Belt-and-braces: true if any overlay is currently open in the DOM. */
function isOverlayOpenInDom(): boolean {
  if (typeof document === "undefined") return false;
  return document.querySelector(OPEN_OVERLAY_SELECTOR) !== null;
}

function isSuppressed(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split("; ")
    .some((entry) => entry.startsWith(`${COOKIE_KEY}=`));
}

function suppressForWindow() {
  document.cookie = `${COOKIE_KEY}=1; path=/; max-age=${SUPPRESS_DAYS * 24 * 60 * 60}; samesite=lax`;
}

export interface NeverBuiltSurveyProps {
  /**
   * True only when the trigger should be live: a signed-in user whose projects
   * have loaded and who has none (never started a build). The parent also keeps
   * this false until it actually knows the project count, so the survey can't
   * fire before we're sure the user qualifies.
   */
  enabled: boolean;
  /** Signup time (auth created_at) in epoch ms, for the 24h trigger + analytics. */
  signupAtMs: number | null;
  /** Whether the user is on a paid plan — part of the consistent survey schema. */
  hasUpgraded: boolean;
}

export function NeverBuiltSurvey({
  enabled,
  signupAtMs,
  hasUpgraded,
}: NeverBuiltSurveyProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const triggerRef = useRef<NeverBuiltTriggerSource>("exit_intent");

  const enabledRef = useRef(enabled);
  const lastFiredAtRef = useRef(0);
  useEffect(() => {
    enabledRef.current = enabled;
  });

  const timeSinceSignup = useCallback(
    () => (signupAtMs ? Date.now() - signupAtMs : null),
    [signupAtMs],
  );

  // Open the survey for a given trigger, standing down if anything else is up.
  // Returns true only if it actually opened (so the exit-intent cooldown only
  // arms on a real show).
  const tryOpen = useCallback(
    (source: NeverBuiltTriggerSource): boolean => {
      if (!enabledRef.current) return false;
      if (isSuppressed()) return false;
      if (
        hasHigherPriorityModalOpen(MODAL_IDS.neverBuilt) ||
        isAnyModalOpen(MODAL_IDS.neverBuilt) ||
        isOverlayOpenInDom()
      ) {
        return false;
      }
      triggerRef.current = source;
      setText("");
      setOpen(true);
      // Show at most once per window — set the cookie the moment it surfaces so
      // a refresh-happy user isn't nagged every session.
      suppressForWindow();
      trackNeverBuiltSurveyShown({
        trigger_source: source,
        time_since_signup_ms: timeSinceSignup(),
      });
      return true;
    },
    [timeSinceSignup],
  );

  // Trigger B — no build 24h after signup, on a later session. Fires once, a
  // beat after load, only when the account is old enough and qualifies.
  useEffect(() => {
    if (!enabled) return;
    if (isSuppressed()) return;
    const age = timeSinceSignup();
    if (age === null || age < MIN_AGE_FOR_24H_PROMPT_MS) return;
    const t = window.setTimeout(() => tryOpen("no_build_24h"), PROMPT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [enabled, timeSinceSignup, tryOpen]);

  // Trigger A — top-edge exit intent (desktop). Same conservative detection as
  // the pre-publish exit survey: only a genuine leave through the top edge, with
  // a cooldown, never from in-page UI.
  useEffect(() => {
    const onMouseOut = (event: MouseEvent) => {
      if (!enabledRef.current) return;
      if (document.hidden) return;
      if (event.clientY > TOP_EDGE_THRESHOLD_PX) return;
      if (
        event.relatedTarget ||
        (event as MouseEvent & { toElement?: EventTarget | null }).toElement
      ) {
        return;
      }
      const now = Date.now();
      if (now - lastFiredAtRef.current < 60_000) return;
      if (tryOpen("exit_intent")) lastFiredAtRef.current = now;
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, [tryOpen]);

  // Report open state so other modals don't stack on top of this survey.
  useEffect(() => {
    setModalOpen(MODAL_IDS.neverBuilt, open);
    return () => setModalOpen(MODAL_IDS.neverBuilt, false);
  }, [open]);

  // Yield to a higher-priority modal that opens after us (render-phase compare,
  // matching the pre-publish survey). The suppression cookie is already set, so
  // closing here just steps aside cleanly.
  const supersededByHigher = useHasHigherPriorityModalOpen(MODAL_IDS.neverBuilt);
  if (open && supersededByHigher) {
    setOpen(false);
  }

  const skip = useCallback(() => {
    trackNeverBuiltSurveySkipped({ trigger_source: triggerRef.current });
    setOpen(false);
  }, []);

  const submit = useCallback(async () => {
    const value = text.trim();
    if (!value || submitting) return;
    setSubmitting(true);
    const { session_id, session_replay_url } = getSessionContext();
    try {
      await fetch("/api/surveys/never-built", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          surveyText: value,
          triggerSource: triggerRef.current,
          sessionId: session_id ?? null,
          sessionReplayUrl: session_replay_url ?? null,
          // Never-built cohort: no build, nothing published. Upgrade state is
          // real so an upgraded-but-never-built user is still distinguishable.
          hasBuilt: false,
          hasPublished: false,
          hasUpgraded,
        }),
      });
    } catch {
      // Fire-and-forget from the user's view — never block closing the modal on
      // a flaky network. The response is best-effort feedback.
    } finally {
      setSubmitting(false);
      setOpen(false);
    }
  }, [text, submitting, hasUpgraded]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submitting) skip();
      }}
    >
      <DialogContent className="max-w-[440px] gap-5">
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">
            Before you go
          </p>
          <h2 className="text-[18px] font-semibold leading-snug text-zinc-900 dark:text-white">
            What stopped you from building?
          </h2>
          <p className="text-[14px] leading-relaxed text-zinc-500 dark:text-zinc-400">
            You signed up but have not started a build yet. Takes 30 seconds.
          </p>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Tell us what got in the way..."
          rows={4}
          autoFocus
          className="w-full resize-none rounded-lg border border-zinc-200 bg-white px-3.5 py-3 text-[14px] leading-relaxed text-zinc-900 outline-none transition-colors placeholder:text-zinc-400 focus:border-emerald-600/60 dark:border-white/10 dark:bg-white/[0.03] dark:text-white dark:focus:border-emerald-500/60"
        />

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={skip}
            disabled={submitting}
            className="rounded-full px-3 py-2 text-[13px] font-normal text-zinc-500 transition-colors hover:bg-zinc-50 hover:text-zinc-800 disabled:opacity-40 dark:text-zinc-400 dark:hover:bg-white/[0.04] dark:hover:text-zinc-100"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!text.trim() || submitting}
            className="flex items-center justify-center gap-1.5 rounded-full bg-[#080B12] px-5 py-2.5 text-[14px] font-medium text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-[#080B12]"
          >
            {submitting ? "Sending..." : "Send feedback"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
